import { parseSseStream } from './sseParser'
import type { StreamEvent } from './streamReducer'
import type { ChatTransport, TurnContext } from './transport'

/**
 * `ChatTransport` for the Azure AI Foundry agent, through the Keystone API
 * (adr/015). `POST /api/chat` calls the agent as the API's own identity and
 * streams its Responses API events back unchanged, so `parseSseStream` still
 * reads Foundry's event names. The request carries the person's normal API
 * token; nobody needs an Azure role to chat.
 *
 * Conversations chain by passing the previous response id rather than a
 * server-side thread, so this transport is stateful w.r.t. `threadId` and
 * ignores `ctx.history`. The API adds who the person is on a chain's first
 * turn.
 */

const chatUrl = `${import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '')}/api/chat`

export const foundryTransport: ChatTransport = {
  async *startTurn(message: string, ctx: TurnContext) {
    let headers: Record<string, string>
    try {
      headers = await ctx.getAuthHeaders()
    } catch (err) {
      yield {
        kind: 'stream.error',
        message: err instanceof Error ? err.message : 'Token acquisition failed',
      }
      return
    }
    const body = ctx.threadId ? { message, previousResponseId: ctx.threadId } : { message }
    yield* streamResponse(body, headers, ctx.signal)
  },
}

/**
 * POST the turn and yield parsed `StreamEvent`s. Network / non-2xx /
 * empty-body failures surface as a single `stream.error` event; an aborted
 * request ends the stream silently (the user cancelled or reset). Never
 * throws, so the hook's consume loop stays uniform.
 */
async function* streamResponse(
  body: { message: string; previousResponseId?: string },
  headers: Record<string, string>,
  signal: AbortSignal,
): AsyncGenerator<StreamEvent> {
  try {
    const response = await fetch(chatUrl, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(body),
      signal,
    })

    if (!response.ok) {
      // The API's error envelope; anything else falls back to the status.
      const err = (await response.json().catch(() => null)) as { message?: unknown } | null
      yield {
        kind: 'stream.error',
        message:
          typeof err?.message === 'string' ? err.message : `Chat failed (HTTP ${response.status})`,
      }
      return
    }

    if (!response.body) {
      yield { kind: 'stream.error', message: 'Response body is empty' }
      return
    }

    for await (const event of parseSseStream(response.body)) {
      if (signal.aborted) break
      yield event
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return
    yield {
      kind: 'stream.error',
      message: err instanceof Error ? err.message : 'Stream failed',
    }
  }
}
